<template>
  <div class="config-lock">
    <!-- Lock bar (stays OUTSIDE the disabled fieldset so "Lås opp" is always clickable). -->
    <div class="config-lock-bar"
         :class="readOnly ? 'config-lock-bar--readonly' : (locked ? 'config-lock-bar--locked' : 'config-lock-bar--open')">
      <font-awesome-icon :icon="['fas', locked ? 'lock' : 'lock-open']" class="config-lock-bar__icon" />
      <span class="config-lock-bar__text">
        <template v-if="readOnly">{{ readOnlyText || 'Skrivebeskyttet her — ingen endringer kan lagres fra dette miljøet.' }}</template>
        <template v-else-if="locked">Låst — allerede konfigurert. Trykk «Lås opp» for å redigere.</template>
        <template v-else>Redigering aktiv — endringer lagres først når du trykker «Lagre».</template>
      </span>
      <!-- readOnly has no unlock: it is not a "are you sure" guard, it is a statement that this browser
           must not write here at all. Offering the button would only teach people to click it. -->
      <button v-if="readOnly" type="button" class="bo_btn_secondary config-lock-bar__btn" disabled
              title="Kan ikke låses opp herfra">
        <font-awesome-icon :icon="['fas', 'lock']" /> Skrivebeskyttet
      </button>
      <button v-else-if="locked" type="button" class="bo_btn_secondary config-lock-bar__btn" @click="unlock">
        <font-awesome-icon :icon="['fas', 'lock-open']" /> Lås opp
      </button>
      <button v-else type="button" class="bo_btn_secondary config-lock-bar__btn" @click="lock">
        <font-awesome-icon :icon="['fas', 'lock']" /> Lås
      </button>
    </div>

    <!-- A disabled <fieldset> natively disables EVERY form control inside the slot — one switch locks
         the whole form without touching each input. Reset so it's a pure wrapper (no border/margin). -->
    <fieldset :disabled="locked" class="config-lock-fieldset">
      <slot :locked="locked" />
    </fieldset>
  </div>
</template>

<script>
/**
 * Guards a System → Config page against accidental edits. When the config is already `configured`, the
 * form loads LOCKED (read-only) and the user must click "Lås opp" to edit. A brand-new/unconfigured
 * config is editable immediately. Session-only (re-locks on reload). Usage:
 *
 *   <config-lock ref="lock" :configured="isConfigured">
 *     ...form fields... + the Lagre button (auto-disabled while locked)
 *   </config-lock>
 *
 * After a successful save, call `this.$refs.lock.relock()` to snap back to the safe locked state.
 *
 * `readOnly` is a different thing from `configured`: not "are you sure", but "this browser must not write
 * here at all". It locks shut with no unlock button, and `configured` cannot re-open it. Used by MDM →
 * Policyer, where a Backoffice on localhost shares production's AMAPI enterprise, so a save would push
 * policies to the live fleet. Pass `readOnlyText` to say why.
 */
export default {
  name: "ConfigLock",
  emits: ["unlock", "lock", "lock-change"],
  props: {
    configured: { type: Boolean, default: false },
    // Locked shut: no unlock button, and `configured` cannot re-open it. For a form this browser must not
    // write to at all — e.g. a dev Backoffice sharing production's AMAPI enterprise.
    readOnly: { type: Boolean, default: false },
    readOnlyText: { type: String, default: "" }
  },
  data() {
    return { locked: this.readOnly || this.configured, userToggled: false };
  },
  watch: {
    // `configured` is resolved after the async config load; adopt it as the initial lock state until the
    // user toggles the lock themselves (then their choice wins).
    configured(val) {
      if (this.readOnly) return;
      if (!this.userToggled) this.locked = val;
    },
    readOnly(val) {
      if (val) this.locked = true;
    },
    // Mirror the lock state out so a parent can gate controls that live OUTSIDE the fieldset
    // (e.g. an action button in another section). Fires on every change (init/unlock/lock/relock).
    locked(val) {
      this.$emit("lock-change", val);
    }
  },
  mounted() {
    this.$emit("lock-change", this.locked);   // publish the initial state
  },
  methods: {
    unlock() { if (this.readOnly) return; this.locked = false; this.userToggled = true; this.$emit("unlock"); },
    lock() { this.locked = true; this.userToggled = true; this.$emit("lock"); },
    // Parent calls this after a successful save to return to the safe locked state.
    relock() { this.locked = true; this.userToggled = false; },
    /**
     * Re-derive the lock from `configured`, discarding any manual unlock.
     *
     * For a page whose form swaps to a DIFFERENT record — the accounting pages have a location selector,
     * and each location is its own connection. relock() is wrong in one direction there (a test
     * connection would load locked) and doing nothing is wrong in the other (an unlock granted for one
     * location would silently carry over to the next one, which may be production).
     */
    reset() { this.locked = this.readOnly || this.configured; this.userToggled = false; }

  }
};
</script>

<style scoped>
.config-lock-bar {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.5rem 0.8rem;
  border-radius: 8px;
  margin: 0.75rem 0 1rem;
  font-size: 0.86rem;
}
.config-lock-bar--locked {
  background: var(--bo-bad-bg);
  border: 1px solid #f3c9c9;
  color: #7a2e2e;
}
.config-lock-bar--readonly {
  background: var(--bo-brand-tint);
  border: 1px solid #ffd591;
  color: var(--bo-brand-ink);
}
.config-lock-bar--open {
  background: var(--bo-ok-bg);
  border: 1px solid #bfe6c8;
  color: #256b34;
}
.config-lock-bar__icon {
  font-size: 1rem;
  flex: 0 0 auto;
}
.config-lock-bar__text {
  line-height: 1.3;
}
.config-lock-bar__btn {
  margin-left: auto;
  flex: 0 0 auto;
}
/* Neutralize the fieldset so it only disables — no default border/margin/padding, and min-inline-size:0
   so it never forces its content wider than the container (a known fieldset gotcha). */
.config-lock-fieldset {
  border: 0;
  margin: 0;
  padding: 0;
  min-inline-size: 0;
}
/* Make the locked (disabled) state read visibly read-only. */
.config-lock-fieldset:disabled {
  opacity: 0.72;
}
</style>
